import { Component, ElementRef, HostListener, Input, OnChanges } from '@angular/core';
import * as XLSX from 'xlsx';

import { ReportResultColumn } from '../../../services/report-query';

@Component({
  selector: 'app-report-spreadsheet',
  standalone: false,
  templateUrl: './report-spreadsheet.html',
  styleUrl: './report-spreadsheet.scss',
})
export class ReportSpreadsheet implements OnChanges {
  @Input() title = 'Report';
  @Input({ required: true }) columns: ReportResultColumn[] = [];
  @Input({ required: true }) rows: Array<Record<string, unknown>> = [];

  grid: unknown[][] = [];
  downloadMenuOpen = false;

  constructor(private readonly host: ElementRef<HTMLElement>) {}

  ngOnChanges(): void {
    this.grid = [
      ...this.rows.map(row => this.columns.map(column => row[column.key] ?? null)),
      this.emptyRow(),
    ];
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.host.nativeElement.contains(event.target as Node)) this.downloadMenuOpen = false;
  }

  @HostListener('document:keydown.escape')
  closeDownloadMenu(): void {
    this.downloadMenuOpen = false;
  }

  columnLetter(index: number): string {
    let current = index + 1;
    let label = '';
    while (current > 0) {
      current -= 1;
      label = String.fromCharCode(65 + (current % 26)) + label;
      current = Math.floor(current / 26);
    }
    return label;
  }

  cellValue(rowIndex: number, columnIndex: number): string {
    const value = this.grid[rowIndex]?.[columnIndex];
    return value === null || value === undefined ? '' : String(value);
  }

  updateCell(rowIndex: number, columnIndex: number, value: string): void {
    const type = this.columns[columnIndex]?.data_type;
    if (value === '') {
      this.grid[rowIndex][columnIndex] = null;
    } else if (type === 'integer' || type === 'number' || type === 'currency' || type === 'percentage') {
      const number = Number(value);
      this.grid[rowIndex][columnIndex] = Number.isFinite(number) ? number : value;
    } else if (type === 'boolean') {
      this.grid[rowIndex][columnIndex] = value.toLowerCase() === 'true';
    } else {
      this.grid[rowIndex][columnIndex] = value;
    }
    if (rowIndex === this.grid.length - 1 && this.hasContent(this.grid[rowIndex])) {
      this.grid = [...this.grid, this.emptyRow()];
    }
  }

  removeRow(index: number): void {
    this.grid = this.grid.filter((_, rowIndex) => rowIndex !== index);
    if (!this.grid.length || this.hasContent(this.grid[this.grid.length - 1])) {
      this.grid = [...this.grid, this.emptyRow()];
    }
  }

  download(format: 'csv' | 'xlsx'): void {
    this.downloadMenuOpen = false;
    const exportRows = this.grid.filter((row, index) => index < this.grid.length - 1 || this.hasContent(row));
    const values = [
      this.columns.map(column => column.label),
      ...exportRows.map(row => row.map(value => format === 'csv' ? this.csvSafe(value) : value)),
    ];
    const worksheet = XLSX.utils.aoa_to_sheet(values);
    worksheet['!cols'] = this.columns.map((column, index) => ({
      wch: Math.min(40, Math.max(column.label.length + 2, ...exportRows.map(row => String(row[index] ?? '').length + 2))),
    }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, this.sheetName());
    XLSX.writeFile(workbook, `${this.fileName()}.${format}`, { bookType: format });
  }

  private emptyRow(): unknown[] {
    return this.columns.map(() => null);
  }

  private hasContent(row: unknown[]): boolean {
    return row.some(value => value !== null && value !== undefined && value !== '');
  }

  private csvSafe(value: unknown): unknown {
    if (typeof value !== 'string' || !/^[=+\-@]/.test(value)) return value;
    return `'${value}`;
  }

  private sheetName(): string {
    return (this.title || 'Report').replace(/[\\/?*\[\]:]/g, ' ').trim().slice(0, 31) || 'Report';
  }

  private fileName(): string {
    return (this.title || 'report').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 80) || 'report';
  }

}
